import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, Icon, InputLabel, Link, Radio, RadioGroup, Typography, useMediaQuery } from "@mui/material";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { FocusEvent, FormEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PRIMARY_COLOR } from "../../../_const/color.const";
import { ServiceTypeEnum } from "../../../_common/enum/setviceTypeEnum";
import { NavButtons } from "../userJourneyComponents/navButtons";
import { ICustomerAppointmentManage } from "../../../_common/interface/customerAppointment/iCustomerAppointmentManage";
import { PrimaryButton } from "../../../_components/button/primary.button";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css'
import StyledTextValidator from "../../../_components/textValidator/styledTextValidator";
import StyledTextValidatorArea from "../../../_components/textValidator/styledTextValidatorArea";
import { CheckBox, CheckBoxOutlineBlankOutlined } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { consumeSkipDetailsLeave, getTrackedFieldName, getTrackedFieldValue, startNewJourney, trackJourneyEvent, valueMeta } from "../../../_helpers/journeyTracking.helper";

dayjs.extend(customParseFormat);

const TRACK_STEP = "customer_details";


export const CustomerDetailsStep = (params: {
    data: ICustomerAppointmentManage,
    onChange(event: any): void,
    serviceType: ServiceTypeEnum,
    servicePrice: number,
    onApplyDiscountCode(): void,
    isDiscountCodeApplied: boolean,
    isDiscountCodeValid: boolean,
    onBack(): void,
    onNext(): void,
    appointmentDate?: Date | null
}) => {

    const [dateOfBirthDate, setDateOfBirthDate] = useState<Dayjs | null>(null);
    const [dateOfBirthInput, setDateOfBirthInput] = useState('');
    const dobInputRef = useRef<HTMLInputElement | null>(null);
    const dobCursorRef = useRef<number | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [isPreviousCreditIssues, setIsPreviousCreditIssues] = useState('No');
    const [isPhoneValid, setIsPhoneValid] = useState(true);
    const [isPhoneNoEntered, setIsPhoneNoEntered] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [termsError, setTermsError] = useState<string | null>(null);
    const [submitMessages, setSubmitMessages] = useState<string[]>([]);
    const validatorMessagesRef = useRef<string[]>([]);
    const [isDisableAllNav, setIsDisableAllNav] = useState(false);
    const requiredMessage = 'This field is required';
    const termsRequiredMessage = 'Tick this box to agree. It is required to continue.';
    const phoneInvalidMessage = 'Enter a valid mobile number. It can start with 08, 8, 00353 or +353.';
    const fieldLabels: { [key: string]: string } = {
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        occupation: 'Occupation',
        appointmentDiscussionNotes: 'What you would like to discuss',
        notes: 'Notes',
        companyName: 'Company name'
    };
    const dobFormatMessage = 'Enter a valid date of birth (DD/MM/YYYY)';
    const dobAgeMessage = 'You must be 18 or over';

    // Journey tracking bookkeeping (refs only - never state, so tracking can never cause a re-render)
    const trackMountedAtRef = useRef<number>(Date.now());
    const trackFocusStartRef = useRef<{ [field: string]: number }>({});
    const trackChangedFieldsRef = useRef<{ [field: string]: boolean }>({});
    const trackLastValidationAtRef = useRef<number>(0);
    const trackAppointmentIdRef = useRef<number | null>(params.data.id);
    const trackDataRef = useRef(params.data);
    const trackDobInputRef = useRef(dateOfBirthInput);
    const trackDobDateRef = useRef(dateOfBirthDate);
    const trackChangeTimersRef = useRef<{ [field: string]: ReturnType<typeof setTimeout> }>({});
    trackAppointmentIdRef.current = params.data.id;
    trackDataRef.current = params.data;
    trackDobInputRef.current = dateOfBirthInput;
    trackDobDateRef.current = dateOfBirthDate;

    const isMobile = useMediaQuery('(max-width:600px)');
    const isMobileOrTablet = useMediaQuery(
        '(max-width:1199px), ((hover: none) and (pointer: coarse))',
        { noSsr: true }
    );

    const maxDate = dayjs().subtract(18, 'year').startOf('day');
    const minDate = dayjs().subtract(104, 'year').startOf('day');

    function formatDobInput(value: string): string {
        const digits = value.replace(/\D/g, '').slice(0, 8);
        if (digits.length <= 2) {
            return digits;
        }
        if (digits.length <= 4) {
            return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }

    function parseDobInput(value: string): Dayjs | null {
        const parsed = dayjs(value, 'DD/MM/YYYY', true);
        if (!parsed.isValid()) {
            return null;
        }
        return parsed;
    }

    function getDobValidationMessage(value: Dayjs | null, typedValue?: string): string | null {
        if (!value) {
            return typedValue ? dobFormatMessage : requiredMessage;
        }
        if (value.isAfter(maxDate, 'day')) {
            return dobAgeMessage;
        }
        if (value.isBefore(minDate, 'day') || value.isAfter(dayjs(), 'day')) {
            return dobFormatMessage;
        }
        return null;
    }


    useEffect(() => {
        if (params.data.isPreviousCreditIssues != null) {
            if (params.data.isPreviousCreditIssues === true)
                setIsPreviousCreditIssues('Yes');
            else
                setIsPreviousCreditIssues('No');
        }
    }, []);

    function snapshotDetailsValues(reason: string) {
        try {
            const d = trackDataRef.current;
            const dob = trackDobInputRef.current
                || (trackDobDateRef.current?.isValid() ? trackDobDateRef.current.format('DD/MM/YYYY') : d.dateOfBirth);
            const fields: { [key: string]: any } = {
                firstName: d.firstName,
                lastName: d.lastName,
                email: d.email,
                dateOfBirth: dob,
                occupation: d.occupation,
                mobilePhoneNo: d.mobilePhoneNo,
                notes: d.notes,
                companyName: d.companyName,
                appointmentDiscussionNotes: d.appointmentDiscussionNotes,
                discountCode: d.discountCode,
                isTermsAndConditionsAgree: d.isTermsAndConditionsAgree,
                isSignupNewsletter: d.isSignupNewsletter,
                isPreviousCreditIssues: d.isPreviousCreditIssues
            };
            Object.keys(fields).forEach((field) => {
                const val = fields[field];
                const asString = val === null || val === undefined || val === false ? (val === false ? "false" : "") : String(val);
                trackJourneyEvent('field_value', {
                    step: TRACK_STEP,
                    field,
                    meta: valueMeta({ reason, value: asString === "true" || asString === "false" || asString.length ? asString : "" }),
                    appointmentId: trackAppointmentIdRef.current
                });
            });
        } catch {
            // analytics only
        }
    }

    // Dwell time on this step. step_view itself is recorded by journey-index on page change.
    useEffect(() => {
        trackMountedAtRef.current = Date.now();
        const onHidden = () => {
            try {
                if (document.visibilityState === "hidden") snapshotDetailsValues("tab_hidden");
            } catch {
                // analytics only
            }
        };
        document.addEventListener("visibilitychange", onHidden);
        return () => {
            document.removeEventListener("visibilitychange", onHidden);
            if (consumeSkipDetailsLeave()) return;
            snapshotDetailsValues("step_leave");
            trackJourneyEvent('step_leave', {
                step: TRACK_STEP,
                meta: `ms=${Date.now() - trackMountedAtRef.current}`,
                appointmentId: trackAppointmentIdRef.current
            });
        };
    }, []);

    // Delegated field tracking. Values are stored in Meta (temporary debug capture).
    function handleTrackFocus(e: FocusEvent<HTMLDivElement>) {
        const field = getTrackedFieldName(e.target);
        if (!field) return;
        trackFocusStartRef.current[field] = Date.now();
        trackJourneyEvent('field_focus', { step: TRACK_STEP, field, appointmentId: trackAppointmentIdRef.current });
    }

    function handleTrackBlur(e: FocusEvent<HTMLDivElement>) {
        const field = getTrackedFieldName(e.target);
        if (!field) return;
        const started = trackFocusStartRef.current[field];
        const value = getTrackedFieldValue(e.target);
        delete trackFocusStartRef.current[field];
        if (trackChangeTimersRef.current[field]) {
            clearTimeout(trackChangeTimersRef.current[field]);
            delete trackChangeTimersRef.current[field];
        }
        trackJourneyEvent('field_blur', {
            step: TRACK_STEP,
            field,
            meta: valueMeta({ ms: started ? Date.now() - started : undefined, value }),
            appointmentId: trackAppointmentIdRef.current
        });
        trackJourneyEvent('field_value', {
            step: TRACK_STEP,
            field,
            meta: valueMeta({ reason: "blur", value }),
            appointmentId: trackAppointmentIdRef.current
        });
    }

    function handleTrackChange(e: FormEvent<HTMLDivElement>) {
        const field = getTrackedFieldName(e.target);
        if (!field) return;
        const value = getTrackedFieldValue(e.target);
        if (!trackChangedFieldsRef.current[field]) {
            trackChangedFieldsRef.current[field] = true;
            trackJourneyEvent('field_change', {
                step: TRACK_STEP,
                field,
                meta: valueMeta({ value }),
                appointmentId: trackAppointmentIdRef.current
            });
        }
        if (trackChangeTimersRef.current[field]) {
            clearTimeout(trackChangeTimersRef.current[field]);
        }
        trackChangeTimersRef.current[field] = setTimeout(() => {
            trackJourneyEvent('field_value', {
                step: TRACK_STEP,
                field,
                meta: valueMeta({ reason: "change", value }),
                appointmentId: trackAppointmentIdRef.current
            });
        }, 500);
    }

    function trackValidationError(field: string, reason: string) {
        trackJourneyEvent('validation_error', { step: TRACK_STEP, field, meta: `reason=${reason}`, appointmentId: trackAppointmentIdRef.current });
    }

    function dobReasonFor(message: string | null): string {
        if (message === dobAgeMessage) return 'age';
        if (message === dobFormatMessage) return 'format';
        return 'required';
    }

    // ValidatorForm reports its own TextValidator failures (required / email) here.
    function handleFormValidationErrors(errors: any[]) {
        try {
            validatorMessagesRef.current = (errors || []).map((err: any) => {
                const field = err?.props?.name;
                const label = fieldLabels[field] || 'A required field';
                if (field) trackValidationError(field, 'form');
                return err?.props?.name === 'email' && params.data.email
                    ? 'Email is not valid'
                    : `${label} is required`;
            });
        } catch {
            // analytics only
        }
        validateFields();
    }

    useEffect(() => {
        if (isMobileOrTablet && dateOfBirthDate?.isValid() && dateOfBirthInput.length !== 10) {
            setDateOfBirthInput(dateOfBirthDate.format('DD/MM/YYYY'));
        }
    }, [isMobileOrTablet]);

    useLayoutEffect(() => {
        const cursor = dobCursorRef.current;
        const input = dobInputRef.current;
        if (cursor === null || !input) {
            return;
        }
        input.setSelectionRange(cursor, cursor);
    }, [dateOfBirthInput]);

    function handleSubmit() {
        snapshotDetailsValues("submit_attempt");
        trackJourneyEvent('submit_attempt', { step: TRACK_STEP, appointmentId: params.data.id });

        validatorMessagesRef.current = [];
        var isInValid = validateFields();

        if (!isInValid) {
            trackJourneyEvent('submit_valid', { step: TRACK_STEP, appointmentId: params.data.id });
            setIsDisableAllNav(true);

            params.onNext();
            Error("");
        }
    }

    function handleBack() {
        snapshotDetailsValues("back_click");
        trackJourneyEvent('back_click', { step: TRACK_STEP, appointmentId: params.data.id });
        trackJourneyEvent('step_leave', {
            step: TRACK_STEP,
            meta: `ms=${Date.now() - trackMountedAtRef.current}`,
            appointmentId: params.data.id
        });
        startNewJourney();
        params.onBack();
    }

    function handleApplyDiscountCode() {
        trackJourneyEvent('discount_apply', { step: TRACK_STEP, appointmentId: params.data.id });
        params.onApplyDiscountCode();
    }


    function handleIsPreviousCreditIssuesChange(event: any) {
        setIsPreviousCreditIssues(event.target.value);
        params.onChange(event);
    }

    function handlePhoneInputChange(e: any) {
        let phoneNo = { name: 'mobilePhoneNo', value: e }
        params.onChange(phoneNo);

        if (!isPhoneValid) {
            if (!e || String(e).trim() === '') {
                setIsPhoneValid(false);
                setPhoneError(requiredMessage);
            } else if (!isValidPhoneNumber(e)) {
                setIsPhoneValid(false);
                setPhoneError(phoneInvalidMessage);
            } else {
                setIsPhoneValid(true);
                setPhoneError(null);
            }
        }
    }

    function handleDateOfBirthChange(event: any) {
        setDateOfBirthDate(event);
        if (event) {
            event.name = "dateOfBirth";
            params.onChange(event);
            try {
                const formatted = event.format ? event.format('DD/MM/YYYY') : String(event);
                trackJourneyEvent('field_value', {
                    step: TRACK_STEP,
                    field: 'dateOfBirth',
                    meta: valueMeta({ reason: "picker", value: formatted }),
                    appointmentId: params.data.id
                });
            } catch {
                // analytics only
            }
        }
    }

    function applyTypedDob(value: string) {
        const parsed = value.length === 10 ? parseDobInput(value) : null;
        const validationMessage = value.length === 10
            ? getDobValidationMessage(parsed, value)
            : null;

        setError(validationMessage);

        if (validationMessage) {
            trackJourneyEvent('field_invalid', { step: TRACK_STEP, field: 'dateOfBirth', meta: `reason=${dobReasonFor(validationMessage)}`, appointmentId: trackAppointmentIdRef.current });
        }

        if (parsed && !validationMessage) {
            handleDateOfBirthChange(parsed);
        } else {
            setDateOfBirthDate(null);
        }
    }

    function handleMobileDobChange(event: any) {
        const input = event.target as HTMLInputElement;
        const previous = dateOfBirthInput;
        const incoming = input.value;
        const incomingCursor = input.selectionStart ?? incoming.length;

        const invalidCharsBeforeCursor = (incoming.slice(0, incomingCursor).match(/[^\d/]/g) || []).length;
        let next = incoming.replace(/[^\d/]/g, '');
        let cursor = Math.max(0, incomingCursor - invalidCharsBeforeCursor);

        const isDeleting = next.length < previous.length;
        const isTypingAtEnd = !isDeleting && cursor >= next.length;

        if (isTypingAtEnd) {
            next = formatDobInput(next);
            cursor = next.length;
        } else if (next.length > 10) {
            next = previous;
            cursor = Math.min(incomingCursor, previous.length);
        }

        dobCursorRef.current = cursor;
        setDateOfBirthInput(next);
        applyTypedDob(next);
    }


    function validateFields() {

        var isError = false;

        // validateFields runs twice per click (NavButtons onNext + ValidatorForm submit/onError);
        // only record analytics once per attempt.
        const now = Date.now();
        const shouldTrack = now - trackLastValidationAtRef.current > 500;
        trackLastValidationAtRef.current = now;

        if (params.data.mobilePhoneNo.trim() === '' || params.data.mobilePhoneNo.length === 0) {
            setIsPhoneValid(false);
            setPhoneError(requiredMessage);
            isError = true;
            if (shouldTrack) trackValidationError('mobilePhoneNo', 'required');
        }
        else if (!isValidPhoneNumber(params.data.mobilePhoneNo)) {
            setIsPhoneValid(false);
            setPhoneError(phoneInvalidMessage);
            isError = true;
            if (shouldTrack) trackValidationError('mobilePhoneNo', 'invalid');
        }
        else {
            setIsPhoneValid(true);
            setPhoneError(null);
        }

        if (params.serviceType !== ServiceTypeEnum.Existing) {
            const parsedDob = isMobileOrTablet
                ? parseDobInput(dateOfBirthInput)
                : dateOfBirthDate;
            const dobError = getDobValidationMessage(parsedDob, isMobileOrTablet ? dateOfBirthInput : undefined);

            if (dobError) {
                setError(dobError);
                isError = true;
                if (shouldTrack) trackValidationError('dateOfBirth', dobReasonFor(dobError));
            } else {
                setError('');
            }
        }

        const messages: string[] = [];
        if (params.data.mobilePhoneNo.trim() === '' || params.data.mobilePhoneNo.length === 0) {
            messages.push('Mobile phone is required');
        } else if (!isValidPhoneNumber(params.data.mobilePhoneNo)) {
            messages.push(phoneInvalidMessage);
        }
        if (params.serviceType !== ServiceTypeEnum.Existing) {
            const parsedDob = isMobileOrTablet ? parseDobInput(dateOfBirthInput) : dateOfBirthDate;
            const dobError = getDobValidationMessage(parsedDob, isMobileOrTablet ? dateOfBirthInput : undefined);
            if (dobError) messages.push(`Date of birth: ${dobError}`);
        }

        if (!params.data.isTermsAndConditionsAgree) {
            setTermsError(termsRequiredMessage);
            messages.push(termsRequiredMessage);
            isError = true;
            if (shouldTrack) trackValidationError('isTermsAndConditionsAgree', 'required');
        } else {
            setTermsError(null);
        }

        const allMessages = [...validatorMessagesRef.current, ...messages];
        setSubmitMessages(allMessages);
        if (allMessages.length > 0) {
            setTimeout(scrollToFirstError, 50);
        }

        return isError;
    }

    function scrollToFirstError() {
        try {
            const root = document.querySelector('.form-side-nav');
            const target = root?.querySelector('.Mui-error, .requiredText, .terms-required');
            if (target && typeof (target as HTMLElement).scrollIntoView === 'function') {
                (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } catch {
            // leave the summary under the button
        }
    }

    function isValidPhoneNumber(phoneNumber: string) {
        const trimmed = phoneNumber.trim().replace(/[\s()-]/g, '');
        if (/^08\d{8}$/.test(trimmed)) return true;
        if (/^8\d{8}$/.test(trimmed)) return true;
        if (/^(?:\+|00)3530?8\d{8}$/.test(trimmed)) return true;
        return false;
    }

    return (
        <>
            <ValidatorForm
                className="form-side-nav"
                onSubmit={handleSubmit}
                onError={handleFormValidationErrors}
                instantValidate
                noValidate>

                {/* Unstyled wrapper: capture-phase handlers for journey tracking only, no layout effect */}
                <div
                    onFocusCapture={handleTrackFocus}
                    onBlurCapture={handleTrackBlur}
                    onChangeCapture={handleTrackChange}
                >
                <Grid
                    container
                    spacing={2}
                    marginLeft={{ xs: "0px", md: "30px" }}
                    paddingRight={{ xs: "36px", md: "92px" }}
                    marginRight={{ xs: "0px", md: "48px" }}
                >
                    <Grid item xs={12}>
                        <Typography
                            fontSize={{ xs: "12px", md: "16px" }}
                            fontFamily="Poppins"
                            fontStyle={{ xs: "normal", md: "italic" }}
                            sx={{ color: "#ACACAC" }}
                        >
                            Fields marked with <span style={{ color: "#2CA9B4" }} >*</span> are mandatory:
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <InputLabel shrink={false}>
                            <Typography
                                fontWeight={{ xs: "500", md: "600" }}
                                fontSize={{ xs: "12px", md: "16px" }}
                                fontFamily="Poppins"
                                sx={{ color: "#282828" }}
                            >First Name:
                                <span style={{ color: "#2CA9B4" }}>*</span>
                            </Typography>
                        </InputLabel>
                        <StyledTextValidator
                            key="firstName"
                            name="firstName"
                            validators={['required']}
                            errorMessages={[requiredMessage]}
                            fullWidth
                            value={params.data.firstName}
                            onChange={(e: any) => params.onChange(e)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <InputLabel shrink={false}>
                            <Typography
                                fontWeight={{ xs: "500", md: "600" }}
                                fontSize={{ xs: "12px", md: "16px" }}
                                fontFamily="Poppins"
                                sx={{ color: "#282828" }}
                            >Last Name:
                                <span style={{ color: "#2CA9B4" }}>*</span>
                            </Typography>
                        </InputLabel>
                        <StyledTextValidator
                            key="lastName"
                            name="lastName"
                            validators={['required']}
                            errorMessages={[requiredMessage]}
                            fullWidth
                            value={params.data.lastName}
                            onChange={(e: any) => params.onChange(e)}
                        />
                    </Grid>


                    {params.serviceType !== ServiceTypeEnum.Existing && <>
                        <Grid item xs={12} md={4}>
                            <InputLabel shrink={false}>
                                <Typography
                                    fontWeight={{ xs: "500", md: "600" }}
                                    fontSize={{ xs: "12px", md: "16px" }}
                                    fontFamily="Poppins"
                                    sx={{ color: "#282828" }}
                                >Date Of Birth:
                                    <span style={{ color: "#2CA9B4" }}>*</span>
                                </Typography>
                            </InputLabel>
                            {isMobileOrTablet ? (
                                <StyledTextValidator
                                    key="dateOfBirth"
                                    name="dateOfBirth"
                                    placeholder="DD/MM/YYYY"
                                    validators={[`required`]}
                                    errorMessages={[requiredMessage]}
                                    fullWidth
                                    value={dateOfBirthInput}
                                    onChange={handleMobileDobChange}
                                    error={Boolean(error)}
                                    helperText={error && error !== requiredMessage ? <>{error}</> : undefined}
                                    inputRef={dobInputRef}
                                    inputProps={{
                                        maxLength: 10
                                    }}
                                />
                            ) : (
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        key="dateOfBirth"
                                        views={['year', 'month', 'day']}
                                        format="DD/MM/YYYY"
                                        disableFuture={true}
                                        value={dateOfBirthDate}
                                        onChange={(e) => handleDateOfBirthChange(e)}
                                        className={`dateOfBirthPicker ${error ? 'error' : ''}`}
                                        slotProps={{
                                            textField: {
                                                helperText: error,
                                                sx: {
                                                    height: '45px',
                                                    border: '2px solid #9CA7B2',
                                                    borderRadius: '15px',
                                                    fontFamily: 'Poppins',
                                                    marginTop: "5px",
                                                    boxShadow: "0px 5px 10px #0000001A",
                                                }
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InputLabel shrink={false}>
                                <Typography
                                    fontWeight={{ xs: "500", md: "600" }}
                                    fontSize={{ xs: "12px", md: "16px" }}
                                    fontFamily="Poppins"
                                    sx={{ color: "#282828" }}
                                >Occupation:
                                    <span style={{ color: "#2CA9B4" }}>*</span>
                                </Typography>
                            </InputLabel>
                            <StyledTextValidator
                                key="occupation"
                                name="occupation"
                                validators={['required']}
                                errorMessages={[requiredMessage]}
                                fullWidth
                                value={params.data.occupation}
                                onChange={(e: any) => params.onChange(e)}
                            />
                        </Grid>
                    </>}
                    <Grid item xs={12} md={params.serviceType == ServiceTypeEnum.Existing ? 6 : 4} sx={{ paddingtop: '16px!important' }}>
                        <InputLabel shrink={false}>
                            <Typography
                                fontWeight={{ xs: "500", md: "600" }}
                                fontSize={{ xs: "12px", md: "16px" }}
                                fontFamily="Poppins"
                                sx={{ color: "#282828" }}
                            >Mobile Phone:
                                <span style={{ color: "#2CA9B4" }}>*</span>
                            </Typography>
                        </InputLabel>
                        <PhoneInput
                            country={'ie'}
                            autoFormat={false}
                            disableCountryCode={true}
                            disableDropdown={true}
                            enableLongNumbers={true}
                            value={params.data.mobilePhoneNo}
                            onChange={handlePhoneInputChange}
                            placeholder="Enter mobile phone number"
                            preferredCountries={['ie']}
                            inputClass={'mobilePhoneNoInput'}
                            isValid={isPhoneValid}
                            inputProps={{
                                name: "phone"
                            }}
                            containerStyle={{
                                height: isMobile ? "32px" : "47px !important",
                                marginBottom: isMobile ? "12px" : "0px"

                            }}
                            inputStyle={{
                                // height: isMobile ? "32px !important" : '47px' ,
                                border: isMobile ? "1px solid #9CA7B2" : '2px solid #9CA7B2',
                                borderRadius: '15px',
                                fontFamily: 'Poppins',
                                marginTop: "5px",
                                boxShadow: isMobile ? "0px 5px 10px #0000000A " : "0px 5px 10px #0000001A",
                            }}

                        />
                        {!isPhoneValid && <div className="requiredText">{phoneError}</div>}
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={params.serviceType == ServiceTypeEnum.Existing ? 6 : 12}
                        pt={{ xs: "0px !important", md: "auto" }}
                        mt={{ sm: "15px" }}>
                        <InputLabel shrink={false}>
                            <Typography
                                fontWeight={{ xs: "500", md: "600" }}
                                fontSize={{ xs: "12px", md: "16px" }}
                                fontFamily="Poppins"
                                sx={{ color: "#282828" }}
                            >Email:
                                <span style={{ color: "#2CA9B4" }}>*</span>
                            </Typography>
                        </InputLabel>
                        <StyledTextValidator
                            id="fabsCustomerDetailsEmailField"
                            key="email"
                            name="email"
                            type="email"
                            validators={['required', 'isEmail']}
                            errorMessages={[requiredMessage, 'Email is not valid']}
                            fullWidth
                            value={params.data.email}
                            onChange={(e: any) => params.onChange(e)}
                        />
                    </Grid>

                    {params.serviceType === ServiceTypeEnum.Mortgage && <Grid item xs={12}>
                        <InputLabel shrink={false}>
                            <Typography
                                fontWeight={{ xs: "500", md: "600" }}
                                fontSize={{ xs: "12px", md: "16px" }}
                                fontFamily="Poppins"
                                sx={{ color: "#282828" }}
                            >Previous Credit Issues:
                                <span style={{ color: "#2CA9B4" }}>*</span>
                            </Typography>
                        </InputLabel>
                        <Grid item xs={12}>

                            <FormControl component="fieldset">
                                <RadioGroup
                                    aria-label="icon-radio"
                                    name="isPreviousCreditIssues"
                                    value={isPreviousCreditIssues}
                                    onChange={(e) => handleIsPreviousCreditIssuesChange(e)}
                                >
                                    <FormControlLabel sx={{
                                        color: "#282828",
                                        " & .MuiTypography-root": {
                                            fontWeight: "600",
                                            fontFamily: "Poppins",
                                            fontSize: { xs: "14px", md: "16px" }
                                        }
                                    }}
                                        value="Yes"
                                        control={
                                            <Radio sx={{ paddingTop: "2px", paddingBottom: "2px", paddingLeft: { xs: "8px", md: "5px" }, paddingRight: "2px" }}
                                                icon={<CheckBoxOutlineBlankOutlined sx={{ color: PRIMARY_COLOR[5], fontSize: { xs: "32px", md: "46px" }, stroke: "#ffffff", strokeWidth: 1 }} />}
                                                checkedIcon={<CheckBox sx={{ color: PRIMARY_COLOR[5], fontSize: { xs: "32px", md: "46px" } }} />}
                                            />
                                        }
                                        label="YES"
                                    />
                                    <FormControlLabel sx={{
                                        color: "#282828",
                                        " & .MuiTypography-root": {
                                            fontWeight: "600",
                                            fontFamily: "Poppins",
                                            fontSize: { xs: "14px", md: "16px" }
                                        }
                                    }} value="No"
                                        control={
                                            <Radio sx={{ paddingTop: "2px", paddingBottom: "2px", paddingLeft: { xs: "8px", md: "5px" }, paddingRight: "2px" }}
                                                icon={<CheckBoxOutlineBlankOutlined sx={{ color: PRIMARY_COLOR[5], fontSize: { xs: "32px", md: "46px" }, stroke: "#ffffff", strokeWidth: 1 }} />} // Icon when unchecked
                                                checkedIcon={<CheckBox sx={{ color: PRIMARY_COLOR[5], fontSize: { xs: "32px", md: "46px" } }} />} // Icon when checked
                                            />
                                        }
                                        label="NO"
                                    />
                                </RadioGroup>
                            </FormControl>

                        </Grid>
                    </Grid>}

                    <Grid item xs={12} marginBottom="20px">
                        {isPreviousCreditIssues === 'Yes' && <>
                            <InputLabel shrink={false}>
                                <Typography whiteSpace="normal"
                                    fontWeight={{ xs: "500", md: "600" }}
                                    fontSize={{ xs: "12px", md: "16px" }}
                                    fontFamily="Poppins"
                                    sx={{ color: "#282828" }}>
                                    Please explain your situation below for us to be able to help you better<span style={{ color: PRIMARY_COLOR[0] }}>*</span>
                                </Typography>
                            </InputLabel>

                            <StyledTextValidatorArea
                                key="notes"
                                name="notes"
                                validators={['required']}
                                errorMessages={[requiredMessage]}
                                fullWidth
                                value={params.data.notes}
                                onChange={(e: any) => params.onChange(e)}
                                inputProps={{ sx: { borderRadius: 4 } }}
                                multiline
                                rows={3}
                                rowsMax={3}
                            />
                            <br>
                            </br>
                            <br>
                            </br>
                        </>}


                        <InputLabel shrink={false} sx={{ marginTop: { xs: "0px", md: "0px" } }}>
                            {params.serviceType !== ServiceTypeEnum.Existing &&
                                <Typography
                                    fontWeight={{ xs: "500", md: "600" }}
                                    fontSize={{ xs: "12px", md: "16px" }}
                                    fontFamily="Poppins"
                                    sx={{ color: "#282828" }}
                                >What do you want to discuss on the call?
                                    <span style={{ color: "#2CA9B4" }}>*</span>
                                </Typography>
                            }
                            {params.serviceType == ServiceTypeEnum.Existing &&
                                <Typography
                                    fontWeight={{ xs: "500", md: "600" }}
                                    fontSize={{ xs: "12px", md: "16px" }}
                                    fontFamily="Poppins"
                                    sx={{ color: "#282828" }}
                                >How can your advisor best help you?
                                    <span style={{ color: "#2CA9B4" }}>*</span>
                                </Typography>
                            }
                        </InputLabel>
                        <StyledTextValidatorArea
                            key="appointmentDiscussionNotes"
                            name="appointmentDiscussionNotes"
                            validators={['required']}
                            errorMessages={[requiredMessage]}
                            fullWidth
                            value={params.data.appointmentDiscussionNotes}
                            onChange={(e: any) => params.onChange(e)}
                            inputProps={{ sx: { borderRadius: 4 } }}
                            multiline
                            rows={3}
                            rowsMax={3}
                        />
                    </Grid>
                    {params.serviceType === ServiceTypeEnum.Financial && <>

                        <Grid item xs={12} marginTop={{ xs: "5px", sm: "0px" }}>
                            <InputLabel shrink={false}>
                                <Typography whiteSpace="normal"
                                    fontWeight={{ xs: "500", md: "600" }}
                                    fontSize={{ xs: "12px", md: "16px" }}
                                    fontFamily="Poppins"
                                    sx={{ color: "#282828" }}
                                >If you're booking with us after taking a money masterclass at your workplace,
                                    please enter your organisations name so we can tailor our services to you
                                </Typography>

                            </InputLabel>
                            <StyledTextValidator
                                key="companyName"
                                name="companyName"
                                fullWidth
                                value={params.data.companyName}
                                onChange={(e: any) => params.onChange(e)}
                            />
                        </Grid>
                    </>}

                    {params.servicePrice > 0 &&
                        <Grid item xs={12}>

                            <Box
                                display="flex"
                                gap={2} mt={2}
                                flexDirection={{ xs: "column", sm: "column", md: "row" }}>
                                <Box>

                                    <InputLabel shrink={false}>
                                        {(params.isDiscountCodeApplied && !params.isDiscountCodeValid) || !params.isDiscountCodeApplied &&
                                            <Typography whiteSpace="normal"
                                                fontWeight={{ xs: "500", md: "600" }}
                                                fontSize={{ xs: "12px", md: "16px" }}
                                                fontStyle={{ xs: "italic", md: "normal" }}
                                                fontFamily="Poppins"
                                                sx={{ color: "#282828" }}
                                            >If you have a discount code, please enter it here:
                                            </Typography>
                                        }
                                        {params.isDiscountCodeApplied && params.isDiscountCodeValid &&
                                            <Typography
                                                fontWeight={{ xs: "500", md: "600" }}
                                                fontSize={{ xs: "14px", md: "16px" }}
                                                fontFamily="Poppins"
                                                sx={{ color: "#282828" }}
                                            >Discount code applied!
                                            </Typography>
                                        }
                                    </InputLabel>
                                    <StyledTextValidator
                                        disabled={params.isDiscountCodeApplied}
                                        key="discountCode"
                                        name="discountCode"
                                        fullWidth
                                        value={params.data.discountCode}
                                        onChange={(e: any) => params.onChange(e)}

                                        helperText={<FormHelperText sx={{ color: '#D32F2F' }} >
                                            {!params.isDiscountCodeValid ? 'Invalid discount code' : ''}
                                        </FormHelperText>}
                                    />
                                </Box>

                                {(params.data.discountCode && params.data.discountCode.length > 0)
                                    && (!params.isDiscountCodeApplied) &&
                                    <Box mt={{ xs: 0, xm: 0, md: 3.5 }}>
                                        <PrimaryButton
                                            disabled={params.isDiscountCodeApplied && params.isDiscountCodeValid}
                                            variant={isMobile ? "fairstoneMobileApplyButton" : "fairstoneApplyButton"}
                                            onClick={handleApplyDiscountCode}>
                                            <Box
                                                fontSize={{ xs: "15px", md: "22px" }}
                                                fontWeight="600"
                                                letterSpacing="1px"
                                                textTransform="none"
                                                display="flex"
                                                justifyContent="center"
                                                alignItems='center'>
                                                Apply
                                            </Box>
                                        </PrimaryButton>
                                    </Box>
                                }


                            </Box>
                        </Grid>
                    }

                    <Grid item xs={12}>
                        <FormControlLabel
                            disabled={isLoading}
                            label={
                                <Typography
                                    fontWeight={{ xs: "500", md: "600" }}
                                    fontSize={{ xs: "10px", md: "13px" }}
                                    fontFamily="Poppins"
                                    sx={{ color: "#282828", paddingTop: "15px" }}
                                >
                                    Keep me updated on your latest giveaways, offers and smart money tips.
                                </Typography>
                            }
                            name="isSignupNewsletter"
                            value={params.data.isSignupNewsletter}
                            onChange={(e) => params.onChange(e)}
                            control={<Checkbox sx={{ paddingRight: "2px" }} value={params.data.isSignupNewsletter} />}
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                '& .MuiCheckbox-root': {
                                    paddingTop: "4px"  // slight nudge down for better alignment if needed
                                },
                                '& .MuiSvgIcon-root': {
                                    color: PRIMARY_COLOR[0],
                                    fontSize: { xs: "32px", md: "46px" },
                                    stroke: "#ffffff",
                                    strokeWidth: 1,
                                }
                            }}
                        />

                        <Box
                            className={termsError ? 'terms-required' : undefined}
                            sx={{
                                border: termsError ? '2px solid #d32f2f' : '2px solid transparent',
                                borderRadius: '12px',
                                backgroundColor: termsError ? '#fff4f4' : 'transparent',
                                padding: termsError ? '8px' : 0
                            }}
                        >
                        <FormControlLabel
                            disabled={isLoading}
                            label={
                                <Typography
                                    fontWeight={{ xs: "500", md: "600" }}
                                    fontSize={{ xs: "10px", md: "13px" }}
                                    fontFamily="Poppins"
                                    sx={{ color: "#282828" }}
                                >
                                    By proceeding, you agree to Fairstone Ireland, including Fairstone Asset Management DAC trading as askpaul, and our
                                    <Link sx={{ color: "#2E99A6", textDecorationColor: "#2E99A6", fontSize: { xs: "10px !important", md: "13px !important" } }} href="https://www.fairstone.ie/our-locations" target="_blank" rel="noopener noreferrer">partner firms </Link>
                                    using your information. Please review our
                                    <Link sx={{ color: "#2E99A6", textDecorationColor: "#2E99A6", fontSize: { xs: "10px !important", md: "13px !important" } }} href="https://www.askpaul.ie/wp-content/uploads/2026/06/AskPaul-Data-Privacy-Notice-June-2026.pdf" target="_blank" rel="noopener noreferrer">data privacy notice </Link>
                                    for details.
                                </Typography>
                            }
                            name="isTermsAndConditionsAgree"
                            value={params.data.isTermsAndConditionsAgree || false}
                            onChange={(e) => {
                                params.onChange(e);
                                setTermsError(null);
                            }}
                            control={<Checkbox sx={{ paddingRight: "2px" }} value={params.data.isTermsAndConditionsAgree} />}
                            sx={{
                                '&.MuiFormControlLabel-root': {
                                    float: { xs: "left", md: "right" },
                                },
                                '&.Mui-checked': {
                                    color: PRIMARY_COLOR[0],
                                },
                                '& .MuiSvgIcon-root': {
                                    color: PRIMARY_COLOR[0],
                                    fontSize: { xs: "32px", md: "46px" },
                                    stroke: "#ffffff",
                                    strokeWidth: 1,

                                }
                            }}
                        />
                        {termsError && (
                            <Typography
                                sx={{
                                    color: '#d32f2f',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: { xs: '16px', md: '18px' },
                                    fontWeight: 700,
                                    marginTop: '8px',
                                    clear: 'both'
                                }}
                            >
                                {termsError}
                            </Typography>
                        )}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <NavButtons
                            showBack={true}
                            showNext={true}
                            isNextComplete={params.servicePrice === 0}
                            isDisableAllNav={isDisableAllNav}
                            isSubmit={true}
                            onBack={handleBack}
                            onNext={validateFields}
                        />
                        {/* Error list under Complete is hidden for now.
                        {submitMessages.length > 0 && (
                            <Box
                                sx={{
                                    marginTop: '16px',
                                    padding: '16px',
                                    border: '2px solid #d32f2f',
                                    borderRadius: '12px',
                                    backgroundColor: '#fff4f4'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 700,
                                        fontSize: { xs: '16px', md: '18px' },
                                        color: '#d32f2f',
                                        marginBottom: '8px'
                                    }}
                                >
                                    Please fix the following before continuing:
                                </Typography>
                                <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
                                    {submitMessages.map((message) => (
                                        <Typography
                                            component="li"
                                            key={message}
                                            sx={{
                                                fontFamily: 'Poppins, sans-serif',
                                                fontSize: { xs: '14px', md: '16px' },
                                                fontWeight: 600,
                                                color: '#282828',
                                                marginBottom: '4px'
                                            }}
                                        >
                                            {message}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        */}
                    </Grid>
                </Grid>
                </div>
            </ValidatorForm>
        </>);
}

